import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * ThemeToggle Component
 * Pill-shaped toggle switch with sun/moon icons
 * 
 * Features:
 * - Smooth sliding animation
 * - Active state shows colored circle with icon
 * - Inactive state shows outline icon
 * - No text label, icon-only design
 */
const ThemeToggle = ({ isCollapsed = false }) => {
  const { theme, toggleTheme, isDark } = useTheme();

  // When collapsed, show only the active icon (no toggle switch)
  if (isCollapsed) {
    return (
      <button
        onClick={toggleTheme}
        className="w-full flex items-center justify-center px-0 py-3 rounded-lg transition-opacity duration-200"
        title={isDark ? 'Dark Mode' : 'Light Mode'}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      >
        {isDark ? (
          // Dark mode active - show moon icon
          <div className="w-6 h-6 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: '#374151' }}>
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </div>
        ) : (
          // Light mode active - show sun icon
          <div className="w-6 h-6 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: '#f59e0b' }}>
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        )}
      </button>
    );
  }

  // When expanded, show full toggle switch
  return (
    <button
      onClick={toggleTheme}
      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-opacity duration-200`}
      title={isDark ? 'Dark Mode' : 'Light Mode'}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {/* Pill-shaped Toggle Track */}
      <div className="relative w-16 h-8 bg-gray-300 rounded-full shadow-inner flex items-center justify-between px-1 flex-shrink-0">
        {/* Sun Icon - Left Side */}
        <div className={`absolute left-1 transition-all duration-300 ${isDark ? 'opacity-50' : 'opacity-100'}`}>
          {isDark ? (
            // Inactive sun (outline)
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            // Active sun (inside golden/orange circle)
            <div className="w-6 h-6 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: '#f59e0b' }}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          )}
        </div>

        {/* Moon Icon - Right Side */}
        <div className={`absolute right-1 transition-all duration-300 ${isDark ? 'opacity-100' : 'opacity-50'}`}>
          {isDark ? (
            // Active moon (inside dark circle)
            <div className="w-6 h-6 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: '#374151' }}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            </div>
          ) : (
            // Inactive moon (outline)
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </div>
      </div>
    </button>
  );
};

export default ThemeToggle;
